import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { Crop } from '@/lib/crop';
import { POND_TABS, tabClosedReason, type PondTab } from './tabs';

interface PondTabsProps {
  pondId: string;
  active: PondTab;
  crop: Crop | null;
}

const base = 'whitespace-nowrap border-b-2 px-1 py-3 text-sm';

export function PondTabs({ pondId, active, crop }: PondTabsProps) {
  const navRef = useRef<HTMLElement>(null);
  const [moreRight, setMoreRight] = useState(false);

  // On a phone the strip is wider than the screen: show that there is more to the right,
  // and keep the open tab in view (deep link, or a tab picked from the timeline).
  const sync = useCallback((scrollToOpen: boolean) => {
    const el = navRef.current;
    if (!el) return;
    if (scrollToOpen) {
      // Not aria-current: the router also marks Overview current on every tab, as the search is a subset.
      const on = el.querySelector<HTMLElement>('[data-open-tab]');
      if (on && on.offsetLeft + on.offsetWidth > el.clientWidth) {
        el.scrollLeft = on.offsetLeft - (el.clientWidth - on.offsetWidth) / 2;
      }
    }
    setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => sync(false);
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    // The web font swaps in after first paint and widens the tabs without resizing the nav.
    let live = true;
    void document.fonts?.ready.then(() => live && sync(true));
    return () => {
      live = false;
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [sync]);

  useEffect(() => sync(true), [active, sync]);

  return (
    <div className="relative min-w-0 border-b border-gray-200">
      {/* relative: the sr-only spans of the greyed-out tabs are absolute and would otherwise escape this clip and widen the page. */}
      <nav ref={navRef} aria-label="Pond sections" className="relative min-w-0 overflow-x-auto">
        <div className="flex gap-6">
          {POND_TABS.map((t) => {
            const closed = tabClosedReason(t.id, crop);
            if (closed) {
              return (
                <span
                  key={t.id}
                  aria-disabled="true"
                  title={closed}
                  className={cn(base, 'cursor-not-allowed border-transparent text-gray-300')}
                >
                  {t.label}
                  <span className="sr-only">: {closed}</span>
                </span>
              );
            }
            const on = t.id === active;
            return (
              <Link
                key={t.id}
                aria-current={on ? 'page' : undefined}
              data-open-tab={on ? '' : undefined}
                to="/ponds/$pondId"
                params={{ pondId }}
                search={(prev) => ({ ...prev, tab: t.id === 'overview' ? undefined : t.id })}
                className={cn(
                  base,
                  on
                    ? 'border-primary-600 font-semibold text-primary-700'
                    : 'border-transparent font-medium text-gray-600 hover:border-gray-300 hover:text-gray-900'
                )}
              >
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>
      {moreRight && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-end bg-gradient-to-l from-gray-50 via-gray-50/90 to-transparent"
        >
          <ChevronRight className="h-4 w-4 text-gray-500" />
        </div>
      )}
    </div>
  );
}
